import { useMemo, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign } from 'lucide-react';

/**
 * The CRM pipeline, one board for every provider (W4, audit B2-crm). The
 * HubSpot deals board and the Swoft opportunities board were two copies of
 * the same markup that had started to drift; both now render this one, with
 * their own stages and cards.
 */

export interface PipelineStage {
  id: string;
  labelKey: string;
  /** Tailwind classes of the column: border, header background, dot, count badge. */
  border: string;
  header: string;
  dot: string;
  badge: string;
}

export interface PipelineStat {
  id: string;
  icon: ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;
  /** Tailwind classes of the icon chip, e.g. "bg-blue-50 text-blue-600". */
  tone: string;
  value: ReactNode;
  label: string;
  sub?: ReactNode;
}

export function PipelineStats({ stats }: { stats: PipelineStat[] }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats.map(({ id, icon: Icon, tone, value, label, sub }) => {
        const [bg, fg] = tone.split(' ');
        return (
          <div key={id} className="bg-card border border-border/50 rounded-xl p-4 flex items-start gap-3">
            <div className={`p-2 rounded-lg ${bg}`}>
              <Icon className={`w-4 h-4 ${fg}`} aria-hidden />
            </div>
            <div>
              <p className="text-xl font-bold text-foreground leading-tight">{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
              {sub !== undefined && sub !== null && sub !== '' && <p className="text-xs text-muted-foreground/60 mt-0.5">{sub}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface PipelineKanbanProps<T> {
  stages: PipelineStage[];
  items: T[];
  /** The stage id of an item; an unknown stage falls into `fallbackStage`. */
  stageOf: (item: T) => string | null | undefined;
  fallbackStage: string;
  keyOf: (item: T) => string;
  renderCard: (item: T) => ReactNode;
}

export function PipelineKanban<T>({ stages, items, stageOf, fallbackStage, keyOf, renderCard }: PipelineKanbanProps<T>) {
  const { t } = useTranslation(['pages', 'common']);
  const byStage = useMemo(() => {
    const known = new Set(stages.map((s) => s.id));
    const map = new Map<string, T[]>(stages.map((s) => [s.id, []]));
    for (const item of items) {
      const s = stageOf(item);
      map.get(s && known.has(s) ? s : fallbackStage)?.push(item);
    }
    return map;
  }, [stages, items, stageOf, fallbackStage]);

  return (
    <div className="w-full overflow-x-auto pb-4" data-testid="pipeline-kanban">
      <div className="flex gap-3 min-w-max px-1">
        {stages.map((stage) => {
          const cards = byStage.get(stage.id) ?? [];
          return (
            <section key={stage.id} aria-label={t(stage.labelKey)} className={`flex flex-col w-56 rounded-2xl border ${stage.border} overflow-hidden flex-shrink-0`}>
              <div className={`${stage.header} px-3 py-2.5 border-b ${stage.border} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${stage.dot} flex-shrink-0`} aria-hidden />
                  <h3 className="text-xs font-semibold text-foreground/80">{t(stage.labelKey)}</h3>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${stage.badge}`}>{cards.length}</span>
              </div>
              <div className="flex flex-col gap-2 p-2 overflow-y-auto" style={{ minHeight: 120, maxHeight: 'calc(100vh - 340px)' }}>
                {cards.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground/40">
                    <DollarSign className="w-5 h-5 mb-1" aria-hidden />
                    <span className="text-xs">{t('common:states.empty')}</span>
                  </div>
                ) : (
                  cards.map((item) => <div key={keyOf(item)}>{renderCard(item)}</div>)
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
